import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { ShieldCheck } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  createApiKey,
  createIntegrationClient,
  getCurrentSubscription,
  listAgents,
  listApiScopeCatalog,
  listIntegrationClients,
  listKnowledge,
} from '../../services/api';
import type {
  Agent,
  ApiKeyApiScopeInput,
  ApiScopeCatalogItem,
  CreateApiKeyInput,
  CreateApiKeyResponse,
  CurrentSubscription,
  IntegrationClient,
  Knowledge,
} from '../../types';
import { useNotification } from '../../hooks/useNotification';
import { useTranslation } from 'react-i18next';
import { enrichCreatedApiKeyScopes } from './curlExamples';
import { API_KEY_SCOPE_PRESETS, API_KEY_USE_CASES, ApiKeyUseCaseId } from './constants';
import {
  CreateApiKeyMetadataFields,
  type MetadataState,
  type RateLimitState,
} from './CreateApiKeyMetadataFields';
import { CreateApiKeyResourceSelect, type NewClientState } from './CreateApiKeyResourceSelect';
import { CreateApiKeyScopeCatalogPicker } from './CreateApiKeyScopeCatalogPicker';
import { CreateApiKeyScopePolicy } from './CreateApiKeyScopePolicy';
import { CreateApiKeyUseCaseSelect } from './CreateApiKeyUseCaseSelect';
import { groupScopeCatalog } from './scopeCatalogUtils';

export interface CreateApiKeyModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (data: CreateApiKeyResponse) => void;
}

export const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { success, error } = useNotification();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [integrationClients, setIntegrationClients] = useState<IntegrationClient[]>([]);
  const [knowledges, setKnowledges] = useState<Knowledge[]>([]);
  const [scopeCatalog, setScopeCatalog] = useState<ApiScopeCatalogItem[]>([]);
  const [selectedCatalogScopes, setSelectedCatalogScopes] = useState<string[]>([]);
  const [selectedScopeRestrictions, setSelectedScopeRestrictions] = useState<
    Record<string, ApiKeyApiScopeInput>
  >({});
  const [scopeSearchTerm, setScopeSearchTerm] = useState('');
  const [resourcesLoading, setResourcesLoading] = useState(false);

  // Optional metadata
  const [metadata, setMetadata] = useState<MetadataState>({
    appName: '',
    environment: '',
    description: '',
  });
  const [selectedUseCase, setSelectedUseCase] = useState<ApiKeyUseCaseId>('selectedScopes');
  const [selectedAgentPublicId, setSelectedAgentPublicId] = useState('');
  const [selectedIntegrationClientPublicId, setSelectedIntegrationClientPublicId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [rateLimit, setRateLimit] = useState<RateLimitState>({
    requestsPerMinute: '',
    requestsPerDay: '',
  });
  const [showAdvancedScopes, setShowAdvancedScopes] = useState(false);
  const [advancedScopesJson, setAdvancedScopesJson] = useState('');
  const [newClient, setNewClient] = useState<NewClientState>({ name: '', environment: 'live' });
  const [creatingClient, setCreatingClient] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);

  const hasCodeAgent = Boolean(
    currentSubscription?.canUseCodeAgent ??
    currentSubscription?.package?.capabilities?.some((cap) =>
      ['code_agent', 'code-agent', 'agent.code'].includes(cap.toLowerCase())
    )
  );

  const availableUseCases = useMemo(() => {
    return API_KEY_USE_CASES.filter((useCase) => {
      if (useCase.id === 'cliLogin') {
        return hasCodeAgent;
      }
      return true;
    });
  }, [hasCodeAgent]);

  useEffect(() => {
    if (!resourcesLoading && !hasCodeAgent && selectedUseCase === 'cliLogin') {
      setSelectedUseCase('selectedScopes');
    }
  }, [hasCodeAgent, resourcesLoading, selectedUseCase]);

  const selectedUseCaseConfig = API_KEY_USE_CASES.find((item) => item.id === selectedUseCase);
  const selectedAgent = agents.find((agent) => agent.publicId === selectedAgentPublicId);
  const selectedIntegrationClient = integrationClients.find(
    (client) => client.public_id === selectedIntegrationClientPublicId
  );

  const presetScopes: ApiKeyApiScopeInput[] =
    selectedUseCase === 'cliLogin'
      ? []
      : selectedUseCase === 'selectedScopes'
        ? selectedCatalogScopes.map((scope) => selectedScopeRestrictions[scope] ?? { scope })
        : selectedUseCase === 'chatWithOneAgent'
          ? selectedAgentPublicId
            ? API_KEY_SCOPE_PRESETS.chatWithOneAgent(selectedAgentPublicId)
            : []
          : selectedIntegrationClientPublicId &&
              (selectedUseCase === 'thirdPartyRuntime' ||
                selectedUseCase === 'thirdPartyProvisioning')
            ? API_KEY_SCOPE_PRESETS[selectedUseCase](selectedIntegrationClientPublicId)
            : [];

  const hasAdvancedScopes = advancedScopesJson.trim().length > 0;
  const canSubmit =
    (selectedUseCase === 'cliLogin'
      ? hasCodeAgent
      : presetScopes.length > 0 || (showAdvancedScopes && hasAdvancedScopes)) &&
    !submitting &&
    !resourcesLoading;
  const needsIntegrationClient =
    selectedUseCase === 'thirdPartyRuntime' || selectedUseCase === 'thirdPartyProvisioning';
  const needsAgent = selectedUseCase === 'chatWithOneAgent';
  const resourceName =
    selectedUseCase === 'chatWithOneAgent' ? selectedAgent?.name : selectedIntegrationClient?.name;
  const orderedCatalogGroups = groupScopeCatalog(scopeCatalog, scopeSearchTerm);

  const toggleCatalogScope = (scope: string) => {
    setSelectedCatalogScopes((prev) => {
      if (!prev.includes(scope)) return [...prev, scope];

      setSelectedScopeRestrictions((current) => {
        const next = { ...current };
        delete next[scope];
        return next;
      });
      return prev.filter((item) => item !== scope);
    });
  };

  const setScopeRestriction = (
    scope: string,
    resourceType: 'agent' | 'knowledge',
    resourcePublicId: string
  ) => {
    setSelectedScopeRestrictions((prev) => {
      const next = { ...prev };

      if (!resourcePublicId) {
        delete next[scope];
        return next;
      }

      next[scope] = {
        scope,
        resourceType,
        resourcePublicId,
      };
      return next;
    });
  };

  // Load selectable resources when modal opens.
  useEffect(() => {
    if (!open) return;
    setResourcesLoading(true);
    Promise.all([
      listAgents(),
      listIntegrationClients(),
      listApiScopeCatalog(),
      listKnowledge('user'),
      getCurrentSubscription(),
    ]).then(([agentsRes, clientsRes, scopeCatalogRes, knowledgeRes, subscriptionRes]) => {
      if (agentsRes.success && agentsRes.data) {
        setAgents(agentsRes.data);
      } else {
        error(agentsRes.error || 'Failed to load agents');
      }

      if (clientsRes.success && clientsRes.data) {
        setIntegrationClients(clientsRes.data);
      } else {
        error(clientsRes.error || 'Failed to load integration clients');
      }

      if (scopeCatalogRes.success && scopeCatalogRes.data) {
        setScopeCatalog(scopeCatalogRes.data);
      } else {
        error(scopeCatalogRes.error || 'Failed to load API scope catalog');
      }

      if (knowledgeRes.success && knowledgeRes.data) {
        setKnowledges(knowledgeRes.data);
      } else {
        error(knowledgeRes.error || 'Failed to load knowledge');
      }

      if (subscriptionRes?.success && subscriptionRes.data) {
        setCurrentSubscription(subscriptionRes.data);
      } else {
        setCurrentSubscription(null);
      }

      setResourcesLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setAdvancedScopesJson(presetScopes.length > 0 ? JSON.stringify(presetScopes, null, 2) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedUseCase,
    selectedAgentPublicId,
    selectedIntegrationClientPublicId,
    selectedCatalogScopes,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);

    let apiScopesInput = selectedUseCase === 'cliLogin' ? [] : presetScopes;

    if (selectedUseCase !== 'cliLogin' && showAdvancedScopes && hasAdvancedScopes) {
      try {
        const parsed = JSON.parse(advancedScopesJson) as unknown;
        if (!Array.isArray(parsed)) {
          throw new Error('Advanced scopes must be a JSON array');
        }
        apiScopesInput = parsed as ApiKeyApiScopeInput[];
      } catch (err) {
        error(err instanceof Error ? err.message : 'Invalid advanced scopes JSON');
        setSubmitting(false);
        return;
      }
    }

    const cleanMeta: CreateApiKeyInput['metadata'] = {};
    if (selectedUseCase === 'cliLogin') {
      cleanMeta.purpose = 'cli_login';
      if (!metadata.appName.trim()) cleanMeta.appName = 'AppAI CLI Login Key';
    }
    if (metadata.appName.trim()) cleanMeta.appName = metadata.appName.trim();
    if (metadata.environment.trim()) cleanMeta.environment = metadata.environment.trim();
    if (metadata.description.trim()) cleanMeta.description = metadata.description.trim();

    const requestsPerMinute = Number(rateLimit.requestsPerMinute);
    const requestsPerDay = Number(rateLimit.requestsPerDay);
    const cleanRateLimit: NonNullable<CreateApiKeyInput['rateLimit']> = {};
    if (Number.isFinite(requestsPerMinute) && requestsPerMinute > 0) {
      cleanRateLimit.requestsPerMinute = requestsPerMinute;
    }
    if (Number.isFinite(requestsPerDay) && requestsPerDay > 0) {
      cleanRateLimit.requestsPerDay = requestsPerDay;
    }

    const input: CreateApiKeyInput = {
      scopes: apiScopesInput,
      ...(Object.keys(cleanMeta).length > 0 && { metadata: cleanMeta }),
      ...(expiresAt && { expiresAt: new Date(expiresAt).toISOString() }),
      ...(Object.keys(cleanRateLimit).length > 0 && { rateLimit: cleanRateLimit }),
    };

    const response = await createApiKey(input);

    if (response.success && response.data?.apiKey) {
      onSuccess(enrichCreatedApiKeyScopes(response.data, apiScopesInput));
      resetForm();
    } else {
      error(response.error || t('apiKeys.errors.createFailed'));
    }

    setSubmitting(false);
  };

  const handleCreateIntegrationClient = async () => {
    const name = newClient.name.trim();
    if (!name || creatingClient) return;

    setCreatingClient(true);
    const response = await createIntegrationClient({
      name,
      environment: newClient.environment,
      status: 'active',
    });

    if (response.success && response.data) {
      setIntegrationClients((prev) => [response.data as IntegrationClient, ...prev]);
      setSelectedIntegrationClientPublicId(response.data.public_id);
      setNewClient({ name: '', environment: 'live' });
      success(t('apiKeys.messages.integrationClientCreated'));
    } else {
      error(response.error || t('apiKeys.errors.createIntegrationClientFailed'));
    }
    setCreatingClient(false);
  };

  const resetForm = () => {
    setMetadata({ appName: '', environment: '', description: '' });
    setSelectedUseCase('selectedScopes');
    setSelectedAgentPublicId('');
    setSelectedIntegrationClientPublicId('');
    setSelectedCatalogScopes([]);
    setSelectedScopeRestrictions({});
    setScopeSearchTerm('');
    setExpiresAt('');
    setRateLimit({ requestsPerMinute: '', requestsPerDay: '' });
    setShowAdvancedScopes(false);
    setAdvancedScopesJson('');
    setNewClient({ name: '', environment: 'live' });
  };

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('apiKeys.modal.createTitle')}</DialogTitle>
        <DialogContent className="space-y-6 !pt-3">
          <CreateApiKeyUseCaseSelect
            value={selectedUseCase}
            onChange={setSelectedUseCase}
            availableUseCases={availableUseCases}
            config={selectedUseCaseConfig}
            hasCodeAgent={hasCodeAgent}
            resourcesLoading={resourcesLoading}
          />

          {selectedUseCaseConfig?.resourceRequired && (
            <CreateApiKeyResourceSelect
              config={selectedUseCaseConfig}
              needsIntegrationClient={needsIntegrationClient}
              needsAgent={needsAgent}
              integrationClients={integrationClients}
              agents={agents}
              clientPublicId={selectedIntegrationClientPublicId}
              onClientChange={setSelectedIntegrationClientPublicId}
              agentPublicId={selectedAgentPublicId}
              onAgentChange={setSelectedAgentPublicId}
              newClient={newClient}
              onNewClientChange={setNewClient}
              creatingClient={creatingClient}
              onCreateClient={handleCreateIntegrationClient}
            />
          )}

          {selectedUseCase === 'selectedScopes' && (
            <CreateApiKeyScopeCatalogPicker
              groups={orderedCatalogGroups}
              catalogEmpty={scopeCatalog.length === 0}
              agents={agents}
              knowledges={knowledges}
              selectedCatalogScopes={selectedCatalogScopes}
              selectedScopeRestrictions={selectedScopeRestrictions}
              searchTerm={scopeSearchTerm}
              onSearchChange={setScopeSearchTerm}
              onToggleScope={toggleCatalogScope}
              onSetRestriction={setScopeRestriction}
            />
          )}

          <CreateApiKeyMetadataFields
            metadata={metadata}
            onMetadataChange={setMetadata}
            expiresAt={expiresAt}
            onExpiresAtChange={setExpiresAt}
            rateLimit={rateLimit}
            onRateLimitChange={setRateLimit}
          />

          <CreateApiKeyScopePolicy
            selectedUseCase={selectedUseCase}
            resourceName={resourceName}
            presetScopes={presetScopes}
            scopeCatalog={scopeCatalog}
            needsIntegrationClient={needsIntegrationClient}
            showAdvancedScopes={showAdvancedScopes}
            onShowAdvancedChange={setShowAdvancedScopes}
            advancedScopesJson={advancedScopesJson}
            onAdvancedJsonChange={setAdvancedScopesJson}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            {t('apiKeys.modal.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ShieldCheck size={16} />
              )
            }
          >
            {submitting ? t('apiKeys.modal.creating') : t('apiKeys.modal.createKey')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
